import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useInspectionOperations } from "../../hooks/vehicle/useInspectionOperations";
import { useInspectionService } from "../../service/vehicle/inspectionService";
import InspectionDataTable from "../../components/ui/tables/InspectionDataTable";
import OperatorDataTable from "../../components/ui/tables/OperatorDataTable";
import ResultsModal from "../../components/ui/modals/ResultsModal";
import "../../assets/scss/views/InspectionValidator.scss";
import FileDropzone from "../../components/FileDropZone";
import LoadingAnimation from "../../components/loading/LoadingAnimation";

const InspectionValidator = ({ theme }) => {
  // Estados para manejo de archivos y proceso
  const [files, setFiles] = useState([]);
  const [step, setStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasProcessedData, setHasProcessedData] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(false);

  // Estados para gestión de datos separados
  const [activeTab, setActiveTab] = useState("vehicles");
  const [processedVehicleData, setProcessedVehicleData] = useState([]);
  const [processedOperatorData, setProcessedOperatorData] = useState([]);
  const [vehicleStats, setVehicleStats] = useState(null);
  const [operatorStats, setOperatorStats] = useState(null);
  const [stats, setStats] = useState(null);

  // Estados para selección de datos
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [selectedOperators, setSelectedOperators] = useState([]);

  // Estados para resultados
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [isProcessingDuplicates, setIsProcessingDuplicates] = useState(false);

  // Servicios y utilidades
  const inspectionService = useInspectionService();
  const inspectionOperations = useInspectionOperations();
  const processExcelFilesMutation = inspectionOperations.useProcessExcelFiles();
const saveInspectionsMutation = inspectionOperations.useSaveEntities();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Función para alternar la visibilidad del encabezado
  const toggleHeaderVisibility = () => {
    setHeaderVisible((prev) => !prev);
  };

  // Handlers para archivos
  const handleFileUpload = (newFiles) => {
    setFiles(newFiles);
  };

  const handleRemoveFile = (fileToRemove) => {
    const updatedFiles = files.filter(
      (file) => file.name !== fileToRemove.name
    );
    setFiles(updatedFiles);
  };

  const handleProcessFiles = async () => {
    if (files.length === 0) return;

    setIsProcessing(true);

    try {
      const fileObjects = files.map((fileItem) => fileItem.file || fileItem);
      const response = await processExcelFilesMutation.mutateAsync(fileObjects);

      // Verificar estructura básica de respuesta
      if (response && response.data) {
        // Extraer datos de vehículos
        if (response.data.vehicles && response.data.vehicles.data) {
          setProcessedVehicleData(response.data.vehicles.data);
          setVehicleStats(response.data.vehicles.stats);

          // Selección automática del primer registro de cada vehículo
          const initialVehicleSelections = [];
          response.data.vehicles.data.forEach((group) => {
            if (group.records && group.records.length > 0) {
              initialVehicleSelections.push(group.records[0]);
            }
          });
          setSelectedVehicles(initialVehicleSelections);
        }

        // Extraer datos de operadores
        if (response.data.operators && response.data.operators.data) {
          setProcessedOperatorData(response.data.operators.data);
          setOperatorStats(response.data.operators.stats);

          // Selección automática del primer registro de cada operador
          const initialOperatorSelections = [];
          response.data.operators.data.forEach((group) => {
            if (group.records && group.records.length > 0) {
              initialOperatorSelections.push(group.records[0]);
            }
          });
          setSelectedOperators(initialOperatorSelections);
        }

        // Estadísticas globales
        if (response.data.stats) {
          setStats(response.data.stats);
        }

        setStep(1);
        setHasProcessedData(true);
      } else {
        console.error("Formato de respuesta inesperado:", response);
        enqueueSnackbar("Error en formato de respuesta del servidor", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Error procesando archivos:", error);
      enqueueSnackbar(error.message || "Error al procesar archivos", {
        variant: "error",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Gestión de selección de vehículos
  const handleSelectVehicle = (plate, vehicleData) => {
    setSelectedVehicles((prev) => {
      const filtered = prev.filter((item) => item.plate !== plate);
      return [...filtered, vehicleData];
    });
  };

  // Gestión de selección de operadores
  const handleSelectOperator = (operatorId, operatorData) => {
    setSelectedOperators((prev) => {
      const filtered = prev.filter(
        (item) => item.operator_id_number !== operatorId
      );
      return [...filtered, operatorData];
    });
  };

  // Guardado de datos según el tipo activo
  const handleSaveData = async () => {
  setIsProcessing(true);
  try {
    let results;
    
    results = await saveInspectionsMutation.mutateAsync({
      type: activeTab === 'vehicles' ? 'vehicles' : 'operators',
      data: activeTab === 'vehicles' ? selectedVehicles : selectedOperators
    });
    
    setResults(results);
    setShowResults(true);
  } catch (error) {
    console.error(`Error guardando ${activeTab}:`, error);
    enqueueSnackbar(error.message || `Error al guardar ${activeTab === 'vehicles' ? 'vehículos' : 'operadores'}`, {
      variant: "error",
    });
  } finally {
    setIsProcessing(false);
  }
};

  // Manejo de resultados y actualización de duplicados
  const handleResultsClose = async (updateChanges = false, updates = []) => {
    if (updateChanges && updates && updates.length > 0) {
      try {
        setIsProcessingDuplicates(true);
        setIsProcessing(true);

        // Contador para seguimiento de progreso
        let successCount = 0;
        let errorCount = 0;

        // Procesamiento secuencial para mayor confiabilidad
        for (const update of updates) {
          try {
            await inspectionService.updateDuplicateEntity(update);
            successCount++;
          } catch (updateError) {
            console.error(
              `Error actualizando registro ID ${update.existingId}:`,
              updateError
            );
            errorCount++;
          }
        }

        // Notificaciones de resultados
        if (successCount > 0) {
          enqueueSnackbar(
            `${successCount} ${
              successCount === 1
                ? "registro actualizado"
                : "registros actualizados"
            } correctamente`,
            {
              variant: "success",
            }
          );
        }

        if (errorCount > 0) {
          enqueueSnackbar(
            `${errorCount} ${
              errorCount === 1 ? "registro falló" : "registros fallaron"
            } al actualizar`,
            {
              variant: "warning",
            }
          );
        }

        // Invalidar caché para refrescar datos
        queryClient.invalidateQueries(["inspections"]);
      } catch (error) {
        console.error(
          "Error al procesar actualizaciones de duplicados:",
          error
        );
        enqueueSnackbar("Error al actualizar registros duplicados", {
          variant: "error",
        });
      } finally {
        setIsProcessingDuplicates(false);
        setIsProcessing(false);
      }
    }

    // Limpiar estado y volver al inicio
    setShowResults(false);
    setStep(0);
    setFiles([]);
    setProcessedVehicleData([]);
    setProcessedOperatorData([]);
    setSelectedVehicles([]);
    setSelectedOperators([]);
    setResults(null);
    setStats(null);
    setVehicleStats(null);
    setOperatorStats(null);
    setHasProcessedData(false);
  };

  // Navegación entre pasos
  const goToReviewStep = () => {
    setStep(1);
  };

  const goToUploadStep = () => {
    setStep(0);
  };

  return (
    <div className={`excel-uploader-container-${theme}`}>
      {/* Overlay de carga */}
      {(isProcessing ||
        processExcelFilesMutation.isLoading ||
        saveInspectionsMutation.isLoading ||
        isProcessingDuplicates) && (
        <div className={`loading-overlay-${theme}`}>
          <LoadingAnimation />
        </div>
      )}

      {/* Paso 1: Carga de archivos */}
      {step === 0 && (
        <div className={`uploader-step-${theme}`}>
          <div className="dropzone-wrapper">
            <FileDropzone
              onFileUpload={handleFileUpload}
              onExternalFileRemove={handleRemoveFile}
              externalFiles={files}
              acceptedFileTypes={{
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                  [".xlsx"],
                "application/vnd.ms-excel": [".xls"],
              }}
              maxFileSize={20971520} // 20MB
              title="Arrastra archivos Excel o haz clic para seleccionarlos"
              size="large"
            />
          </div>

          {processExcelFilesMutation.error && (
            <div className={`error-message-${theme}`}>
              {processExcelFilesMutation.error.message}
            </div>
          )}

          <div className={`process-button-container-${theme}`}>
            {hasProcessedData && (
              <button
                className={`secondary-button-${theme}`}
                onClick={goToReviewStep}
                disabled={isProcessing || processExcelFilesMutation.isLoading}
              >
                Ver Datos Procesados
              </button>
            )}

            <button
              className={`primary-button-${theme}`}
              onClick={handleProcessFiles}
              disabled={
                isProcessing ||
                processExcelFilesMutation.isLoading ||
                files.length === 0
              }
            >
              {isProcessing || processExcelFilesMutation.isLoading
                ? "Procesando..."
                : "Procesar Archivos"}
            </button>
          </div>
        </div>
      )}

      {/* Paso 2: Revisión de datos */}
      {step === 1 && (
        <div className={`data-review-step-${theme}`}>
          {/* Toggle para mostrar/ocultar detalles */}
          <div className={`header-toggle-container-${theme}`}>
            <button
              className={`header-toggle-button-${theme}`}
              onClick={toggleHeaderVisibility}
              aria-label={
                headerVisible ? "Ocultar cabecera" : "Mostrar cabecera"
              }
            >
              {headerVisible ? "▲ Ocultar detalles" : "▼ Mostrar detalles"}
            </button>
          </div>

          {/* Sección de estadísticas */}
          <div
            className={`header-content-${theme} ${
              headerVisible ? "visible" : "hidden"
            }`}
          >
            <h2 className={`review-title-${theme}`}>Revisión de Datos</h2>

            {stats && (
              <div className={`stats-container-${theme}`}>
                <div className="stat-item">
                  <span className="stat-label">Archivos procesados:</span>
                  <span className="stat-value">
                    {stats.processedFiles}/{stats.totalFiles}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Vehículos extraídos:</span>
                  <span className="stat-value">
                    {vehicleStats?.totalRecords || 0}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Operadores extraídos:</span>
                  <span className="stat-value">
                    {operatorStats?.totalRecords || 0}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Pestañas para cambiar entre vehículos y operadores */}
          <div className={`tabs-container-${theme}`}>
            <button
              className={`tab-button-${theme} ${
                activeTab === "vehicles" ? "active" : ""
              }`}
              onClick={() => setActiveTab("vehicles")}
            >
              Vehículos
            </button>
            <button
              className={`tab-button-${theme} ${
                activeTab === "operators" ? "active" : ""
              }`}
              onClick={() => setActiveTab("operators")}
            >
              Operadores
            </button>
          </div>

          {/* Tabla condicional según la pestaña activa */}
          {activeTab === "vehicles" ? (
            <InspectionDataTable
              data={processedVehicleData}
              selectedInspections={selectedVehicles}
              onSelectInspection={handleSelectVehicle}
              theme={theme}
              headerVisible={headerVisible}
              dataType="vehicles"
            />
          ) : (
            <OperatorDataTable
              data={processedOperatorData}
              selectedOperators={selectedOperators}
              onSelectOperator={handleSelectOperator}
              theme={theme}
              headerVisible={headerVisible}
            />
          )}

          {/* Botones de acción */}
          <div className={`action-buttons-${theme}`}>
            <button
              className={`secondary-button-${theme}`}
              onClick={goToUploadStep}
              disabled={isProcessing || saveInspectionsMutation.isLoading}
            >
              Volver
            </button>

            <button
              className={`primary-button-${theme}`}
              onClick={handleSaveData}
              disabled={
                isProcessing ||
                saveInspectionsMutation.isLoading ||
                (activeTab === "vehicles"
                  ? selectedVehicles.length === 0
                  : selectedOperators.length === 0)
              }
            >
              {isProcessing || saveInspectionsMutation.isLoading
                ? "Guardando..."
                : `Guardar ${
                    activeTab === "vehicles" ? "Vehículos" : "Operadores"
                  }`}
            </button>
          </div>
        </div>
      )}

      {/* Modal de resultados */}
      {showResults && results && (
        <ResultsModal
          results={results}
          onClose={handleResultsClose}
          isProcessing={isProcessingDuplicates || isProcessing}
          theme={theme}
          type={activeTab === "vehicles" ? "vehicle" : "operator"}
        />
      )}
    </div>
  );
};

export default InspectionValidator;
